// header-hover-details.js (patched)
// Robust hover handler for nested <details> menus.
// - open instantly
// - delays close and uses pointer location + "hot zone" checks to avoid accidental closes
// - supports common theme selectors and falls back to nav/ul detection
// - Escape closes menus
//
// Paste as an asset and include with: <script src="{{ 'header-hover-details.js' | asset_url }}" defer="defer"></script>

(function () {
  const DEBUG = false; // set true to see console logs during debugging

  // Only run on hover-capable, pointer-fine devices
  if (!window.matchMedia || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    if (DEBUG) console.log('header-hover-details: skipping (no hover/pointer-fine)');
    return;
  }

  /* ---------- Config ---------- */
  // root containers (where menus live)
  const ROOT_SELECTOR = '.header__inline-menu, .menu-drawer__navigation, nav, header';

  // close timing (ms)
  const DETAILS_CLOSE = 700;    // default close delay for <details>
  const FLYOUT_CLOSE   = 600;   // longer close delay for flyout panels

  // hot zone margin in pixels (expanded rect around flyout where we won't close)
  const HOT_ZONE_MARGIN = 20;

  // selectors to consider as flyouts / submenu panels
  const FLYOUT_SELECTORS = [
    '.header__inline-menu .header__submenu',
    '.header__inline-menu .mega-menu__content',
    '.header__inline-menu .mega-menu__list',
    '.header__inline-menu .header__submenu ul',
    '.menu-drawer__navigation .menu-drawer__submenu',
    '.menu-drawer__navigation .menu-drawer__menu',
    'nav > ul', 'nav ul', '.submenu', '.sub-menu', '.dropdown-menu', '.megamenu', '.mega-menu'
  ];
  /* ------------------------------ */

  // internal state
  const detailsTimers = new WeakMap();  // details -> timeoutID
  const flyoutTimers  = new WeakMap();  // flyoutNode -> timeoutID
  const boundDetails  = new WeakSet();
  const boundFlyouts  = new WeakSet();

  // store last pointer position (updated on pointermove)
  let lastPointer = { x: -9999, y: -9999 };
  document.addEventListener('pointermove', (ev) => {
    lastPointer.x = ev.clientX;
    lastPointer.y = ev.clientY;
  }, { passive: true });

  // helpers
  function isDescendant(parent, node) {
    if (!parent || !node) return false;
    return parent === node || parent.contains(node);
  }

  function getFlyoutSelector() {
    return FLYOUT_SELECTORS.join(',');
  }

  function expandRect(rect, margin) {
    return {
      left: rect.left - margin,
      right: rect.right + margin,
      top: rect.top - margin,
      bottom: rect.bottom + margin
    };
  }

  function pointInRect(ptX, ptY, rect) {
    return (ptX >= rect.left && ptX <= rect.right && ptY >= rect.top && ptY <= rect.bottom);
  }

  function clearDetailsTimer(det) {
    const t = detailsTimers.get(det);
    if (t) { clearTimeout(t); detailsTimers.delete(det); }
  }
  function clearFlyoutTimer(el) {
    const t = flyoutTimers.get(el);
    if (t) { clearTimeout(t); flyoutTimers.delete(el); }
  }

  function clearAllDetailsTimersInRoot(root) {
    try {
      const dets = root.querySelectorAll ? Array.from(root.querySelectorAll('details')) : [];
      dets.forEach(d => clearDetailsTimer(d));
    } catch (e) { /* ignore */ }
  }
  function clearAllFlyoutTimersInRoot(root) {
    try {
      const sel = getFlyoutSelector();
      const nodes = root.querySelectorAll ? Array.from(root.querySelectorAll(sel)) : [];
      nodes.forEach(n => clearFlyoutTimer(n));
    } catch (e) { /* ignore */ }
  }

  function syncDetailsOpen(det, open) {
    if (!det) return;
    det.open = !!open;
    if (det.open) det.setAttribute('open', '');
    else det.removeAttribute('open');

    const s = det.querySelector && det.querySelector('summary');
    if (s) s.setAttribute('aria-expanded', det.open ? 'true' : 'false');
    det.classList.toggle('hover-open', !!det.open);

    if (DEBUG) console.log('syncDetailsOpen', det, open);
  }
  function openDetails(det) { syncDetailsOpen(det, true); }
  function closeDetails(det) { syncDetailsOpen(det, false); }

  // -------------------------
  // SEARCH-RELATED EXCLUSION
  // -------------------------
  // Helper: detect if a node (detail/flyout) is part of the header search UI
  function isSearchRelated(node) {
    if (!node) return false;

    // direct ancestors that indicate search UI
    if (node.closest && node.closest('.header__search, .search-modal, details-modal, .predictive-search, .search-popup-wrap')) {
      return true;
    }

    // if the node itself matches a search panel selector
    try {
      if (node.matches && (node.matches('.header__search') || node.matches('.search-modal') || node.matches('.predictive-search') || node.matches('details-modal'))) {
        return true;
      }
    } catch (e) {
      /* ignore invalid matches */
    }

    // if it's a <details> or container that has a summary which is the search toggle
    const s = (node.querySelector ? node.querySelector('summary') : null);
    if (s) {
      // summary may have class header__icon--summary or modal__toggle or aria-label for search
      if (s.classList.contains('header__icon--summary') ||
          s.classList.contains('modal__toggle') ||
          /search/i.test(s.getAttribute && (s.getAttribute('aria-label') || '') ) ) {
        return true;
      }
      // summary inside search wrappers
      if (s.closest && s.closest('.header__search, .search-modal, details-modal, .predictive-search, .search-popup-wrap')) return true;
    }

    // safe fallback: check for descendants of known search wrappers
    if (node.matches && node.matches('.header__search *')) return true;

    return false;
  }

  // -------------------------
  // End search-related exclusion
  // -------------------------

  // Returns true if the point (x,y) is inside the element or inside any of the element's flyouts (expanded by HOT_ZONE_MARGIN)
  // NOTE: also treats the <summary> bounding rect as part of the hot area (helps diagonal crossing)
  function isPointInElementOrFlyouts(x, y, det, root) {
    try {
      // If pointer is exactly inside an element that is a descendant of det, treat as inside
      const nodeUnder = document.elementFromPoint(x, y);
      if (isDescendant(det, nodeUnder)) return true;

      // include summary rect to avoid accidental close when crossing the summary element
      const summary = det && det.querySelector ? det.querySelector('summary') : null;
      if (summary) {
        const sr = summary.getBoundingClientRect();
        if (pointInRect(x, y, expandRect(sr, HOT_ZONE_MARGIN))) return true;
      }

      // check any flyouts under this root that are descendants of det
      const selector = getFlyoutSelector();
      let candidates = [];
      if (root && root.querySelectorAll) candidates = Array.from(root.querySelectorAll(selector));
      else candidates = Array.from(document.querySelectorAll(selector));

      for (const fly of candidates) {
        if (!fly) continue;
        // only consider flyouts that are logically children of this details
        if (!isDescendant(det, fly)) continue;
        const r = fly.getBoundingClientRect();
        const expanded = expandRect(r, HOT_ZONE_MARGIN);
        if (pointInRect(x, y, expanded)) return true;
      }
    } catch (e) {
      if (DEBUG) console.error('isPointInElementOrFlyouts error', e);
    }
    return false;
  }

  // When pointer leaves a details, we will schedule a close. But first:
  // - if pointer is currently inside a child flyout (or within expanded hot zone) -> do NOT close
  // - otherwise schedule close after DETAILS_CLOSE
  function onDetailsPointerLeave(det, ev, root) {
    clearDetailsTimer(det);

    const x = (ev && typeof ev.clientX === 'number') ? ev.clientX : lastPointer.x;
    const y = (ev && typeof ev.clientY === 'number') ? ev.clientY : lastPointer.y;

    if (isPointInElementOrFlyouts(x, y, det, root)) {
      if (DEBUG) console.log('pointer left details but pointer is in child/flyout hot zone — not closing', det);
      return;
    }

    const id = setTimeout(() => {
      // double-check at timeout time: if pointer has moved into the element/flyout, cancel
      const currentX = lastPointer.x, currentY = lastPointer.y;
      if (isPointInElementOrFlyouts(currentX, currentY, det, root)) {
        if (DEBUG) console.log('delayed close canceled because pointer moved into flyout', det);
        return;
      }
      // otherwise close if not hovered/focused
      if (!det.matches(':hover') && !det.contains(document.activeElement)) {
        closeDetails(det);
        if (DEBUG) console.log('details closed after delay', det);
      }
    }, DETAILS_CLOSE);

    detailsTimers.set(det, id);
  }

  // Flyout pointer handlers: when leaving flyout, schedule a LONGER close on its ancestor details,
  // but cancel the close if pointer is in the expanded hot zone or moved into the ancestor
  function onFlyoutPointerLeave(node, root) {
    clearFlyoutTimer(node);

    const id = setTimeout(() => {
      const det = node.closest('details');
      const x = lastPointer.x, y = lastPointer.y;
      if (!det) return;
      if (isPointInElementOrFlyouts(x, y, det, root)) {
        if (DEBUG) console.log('flyout close canceled (pointer in hot zone)', node);
        return;
      }
      if (!det.matches(':hover') && !det.contains(document.activeElement)) {
        closeDetails(det);
        if (DEBUG) console.log('flyout triggered closing ancestor detail', det);
      }
    }, FLYOUT_CLOSE);

    flyoutTimers.set(node, id);
  }

  // Close sibling/open details within same root when opening a details.
  // Keep ancestor/descendant relationships intact: don't close ancestors of `det`.
  function closeSiblingDetails(det, root) {
    try {
      const dets = root.querySelectorAll ? Array.from(root.querySelectorAll('details')) : [];
      dets.forEach(d => {
        if (d === det) return;
        // don't close ancestor details (they should remain open)
        if (isDescendant(d, det)) return;
        // don't close descendants of det either
        if (isDescendant(det, d)) return;
        // close and clear timers
        clearDetailsTimer(d);
        closeDetails(d);
      });
      // also clear flyout timers in root (to avoid stale reopens)
      clearAllFlyoutTimersInRoot(root);
    } catch (e) {
      if (DEBUG) console.error('closeSiblingDetails error', e);
    }
  }

  // Attach handlers to a details
  function attachDetails(det, root) {
    if (!det || boundDetails.has(det)) return;

    // SKIP if this detail is part of the search UI
    if (isSearchRelated(det)) {
      if (DEBUG) console.log('header-hover-details: skipping search-related <details>', det);
      return;
    }

    boundDetails.add(det);

    // ensure summary has aria-expanded
    const s = det.querySelector && det.querySelector('summary');
    if (s && !s.hasAttribute('aria-expanded')) {
      s.setAttribute('aria-expanded', det.open ? 'true' : 'false');
    }

    det.addEventListener('pointerenter', (ev) => {
      // when entering, open immediately
      clearDetailsTimer(det);
      // close sibling details in same root to avoid multiple open menus
      closeSiblingDetails(det, root || document);
      openDetails(det);
      if (DEBUG) console.log('pointerenter details -> open', det);
    });

    det.addEventListener('pointerleave', (ev) => {
      onDetailsPointerLeave(det, ev, root);
    });

    // keyboard focus handling
    det.addEventListener('focusin', () => {
      clearDetailsTimer(det);
      closeSiblingDetails(det, root || document);
      openDetails(det);
    });
    det.addEventListener('focusout', (ev) => {
      const related = ev.relatedTarget;
      if (related && det.contains(related)) return;
      onDetailsPointerLeave(det, null, root);
    });
  }

  // Attach handlers to flyout panels within a root
  function attachFlyouts(root) {
    const sel = getFlyoutSelector();
    let nodes = [];
    try {
      nodes = root.querySelectorAll ? Array.from(root.querySelectorAll(sel)) : [];
    } catch (e) {
      // if selector fails, fallback to nav > ul / ul inside root
      if (root.querySelectorAll) nodes = Array.from(root.querySelectorAll('nav ul, ul'));
    }

    nodes.forEach(node => {
      if (!node || boundFlyouts.has(node)) return;

      // SKIP if the flyout is part of the search UI
      if (isSearchRelated(node)) {
        if (DEBUG) console.log('header-hover-details: skipping search-related flyout', node);
        return;
      }

      boundFlyouts.add(node);

      // pointerenter cancels any pending flyout close and opens ancestor details
      node.addEventListener('pointerenter', () => {
        // clear timers for this node and sibling timers to avoid race conditions
        clearFlyoutTimer(node);
        const det = node.closest('details');
        if (det) {
          clearDetailsTimer(det);
          // close sibling details to ensure only this menu remains open
          closeSiblingDetails(det, root || document);
          openDetails(det);
        }
        if (DEBUG) console.log('flyout pointerenter -> keep open', node);
      });

      // pointerleave schedules a long close check
      node.addEventListener('pointerleave', () => {
        onFlyoutPointerLeave(node, root);
      });
    });
  }

  function initScope(scope) {
    const roots = Array.from((scope && scope.querySelectorAll) ? scope.querySelectorAll(ROOT_SELECTOR) : []);
    const effectiveRoots = (roots.length ? roots : [document]);
    effectiveRoots.forEach(root => {
      // attach to <details>
      const dets = root.querySelectorAll ? Array.from(root.querySelectorAll('details')) : [];
      dets.forEach(d => attachDetails(d, root));

      // attach flyouts
      attachFlyouts(root);
    });
    if (DEBUG) console.log('header-hover-details: initialized for roots', effectiveRoots.length);
  }

  // MutationObserver: re-init only if nodes added that may contain menus/details
  const observer = new MutationObserver((mutations) => {
    let should = false;
    for (const m of mutations) {
      if (!m.addedNodes || m.addedNodes.length === 0) continue;
      for (const n of m.addedNodes) {
        if (!(n instanceof Element)) continue;
        if (n.matches && (n.matches(ROOT_SELECTOR) || n.matches('details') || n.querySelector && n.querySelector('details'))) {
          should = true; break;
        }
      }
      if (should) break;
    }
    if (should) initScope(document);
  });

  // Escape to close
  function onKeyDown(e) {
    if (e.key === 'Escape' || e.key === 'Esc') {
      // close all details inside roots
      const roots = Array.from(document.querySelectorAll(ROOT_SELECTOR));
      const targets = (roots.length ? roots : [document]);
      targets.forEach(r => {
        const dets = r.querySelectorAll ? Array.from(r.querySelectorAll('details')) : [];
        dets.forEach(d => { if (d.open) closeDetails(d); });
      });
    }
  }

  // Start
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initScope(document);
      observer.observe(document.body, { childList: true, subtree: true });
      document.addEventListener('keydown', onKeyDown);
      if (DEBUG) console.log('header-hover-details: started (DOMContentLoaded)');
    });
  } else {
    initScope(document);
    observer.observe(document.body, { childList: true, subtree: true });
    document.addEventListener('keydown', onKeyDown);
    if (DEBUG) console.log('header-hover-details: started');
  }

  // cleanup for SPA-like navs
  window.addEventListener('pagehide', () => {
    observer.disconnect();
    document.removeEventListener('keydown', onKeyDown);
  });

})();
